/**
 * TL / match feedback should open as soon as scheduled play starts:
 * - Single-day (non-days-type): at match startDate + startTime.
 * - Days-type: at startTime on the calendar day for that match day.
 */

/**
 * True when current time is strictly before scheduled match start (start date + start time).
 * Used to disable feedback actions until the match has begun.
 * @param {object} opts
 * @param {string|Date} [opts.startDate]
 * @param {string} [opts.startTime]
 * @param {Date} [opts.now]
 * @returns {boolean}
 */
export function isBeforeScheduledMatchStart({ startDate, startTime, now = new Date() }) {
  const startDt = combineDateAndTime(startDate, startTime);
  if (!startDt) return false;
  return now.getTime() < startDt.getTime();
}

/**
 * @param {string|Date} dateValue
 * @param {string} [timeValue] "HH:mm", "HH:mm:ss", or parseable time
 * @returns {Date|null}
 */
export function combineDateAndTime(dateValue, timeValue) {
  if (dateValue == null || dateValue === '') return null;
  const base = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
  if (Number.isNaN(base.getTime())) return null;

  if (timeValue == null || String(timeValue).trim() === '') {
    return base;
  }

  const t = String(timeValue).trim();
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  const hm = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (hm) {
    hours = Number(hm[1]);
    minutes = Number(hm[2]);
    seconds = hm[3] != null ? Number(hm[3]) : 0;
  } else {
    const td = new Date(t);
    if (!Number.isNaN(td.getTime())) {
      hours = td.getHours();
      minutes = td.getMinutes();
      seconds = td.getSeconds();
    }
  }

  const out = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hours, minutes, seconds, 0);
  return out;
}

/**
 * Calendar date for match day N (1-based): startDate + (N - 1) local days.
 * @param {string|Date} startDateValue
 * @param {number} dayNumber1Based
 * @returns {Date|null}
 */
export function getCalendarDateForMatchDay(startDateValue, dayNumber1Based) {
  const day = Number(dayNumber1Based);
  if (!Number.isFinite(day) || day < 1) return null;
  const base = startDateValue instanceof Date ? new Date(startDateValue) : new Date(startDateValue);
  if (Number.isNaN(base.getTime())) return null;
  const localMidnight = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  localMidnight.setDate(localMidnight.getDate() + (day - 1));
  return localMidnight;
}

/**
 * Scheduled start instant for a given match day (days-type matches).
 * @param {string|Date} startDateValue - first day of the match
 * @param {string} startTimeValue - same start time each day
 * @param {number} dayNumber1Based
 * @returns {Date|null}
 */
export function getScheduledStartForMatchDay(startDateValue, startTimeValue, dayNumber1Based) {
  const dayDate = getCalendarDateForMatchDay(startDateValue, dayNumber1Based);
  if (!dayDate) return null;
  return combineDateAndTime(dayDate, startTimeValue);
}

/**
 * Scheduled start for a single continuous match window (non-days-type).
 * Uses startDate + startTime.
 */
export function getScheduledMatchStart(startDateValue, startTimeValue) {
  return combineDateAndTime(startDateValue, startTimeValue);
}

/**
 * @param {object} opts
 * @param {boolean} opts.isDaysMatch
 * @param {string|Date} [opts.startDate]
 * @param {string} [opts.startTime]
 * @param {string|Date} [opts.endDate]
 * @param {string} [opts.endTime]
 * @param {number} [opts.dayNumber] — for days-type, 1-based day index
 * @param {Date} [opts.now]
 * @returns {boolean}
 */
export function isTlFeedbackWindowOpen({
  isDaysMatch,
  startDate,
  startTime,
  endDate: _endDate,
  endTime: _endTime,
  dayNumber = 1,
  now = new Date(),
}) {
  if (isDaysMatch) {
    const startDt = getScheduledStartForMatchDay(startDate, startTime, dayNumber);
    return Boolean(startDt && now.getTime() >= startDt.getTime());
  }
  const startDt = getScheduledMatchStart(startDate, startTime);
  return Boolean(startDt && now.getTime() >= startDt.getTime());
}

/** Human-readable scheduled start for messages (local). */
export function formatScheduledStartLabel(isDaysMatch, startDate, startTime, dayNumber) {
  if (isDaysMatch) {
    const d = getScheduledStartForMatchDay(startDate, startTime, dayNumber);
    if (!d) return '';
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  const d = getScheduledMatchStart(startDate, startTime);
  if (!d) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Day pill label: "Day 1 - 29 Apr" (calendar day from match start + day index).
 * @param {number} dayNumber1Based
 * @param {string|Date} [matchStartDate]
 * @returns {string}
 */
export function formatMatchDayPillLabel(dayNumber1Based, matchStartDate) {
  const n = Number(dayNumber1Based);
  if (!Number.isFinite(n) || n < 1) return `Day ${dayNumber1Based}`;
  if (matchStartDate == null || matchStartDate === '') return `Day ${n}`;
  const d = getCalendarDateForMatchDay(matchStartDate, n);
  if (!d) return `Day ${n}`;
  const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `Day ${n} - ${datePart}`;
}
